import React, { forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { LoaderCircle } from "lucide-react";

const microsoftAuthSchema = z.object({
    MICROSOFT_CLIENT_ID: z.string().min(1, "Microsoft Client ID is required"),
    MICROSOFT_CLIENT_SECRET: z.string().min(1, "Microsoft Client Secret is required"),
    MICROSOFT_TENANT_ID: z.string().min(1, "Microsoft Tenant ID is required"),
});

type MicrosoftAuthData = z.infer<typeof microsoftAuthSchema>;

type Props = {
    data?: MicrosoftAuthData;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
    loading?: boolean;
};

const MicrosoftAuthForm = forwardRef<any, Props>(
    (
        {
            data,
            initialData = {},
            onDataChange,
            readOnly = false,
            error,
            submitButton,
            onSubmit,
            hideSubmitButton = false,
            loading = false,
        },
        ref
    ) => {
        const form = useForm<MicrosoftAuthData>({
            resolver: zodResolver(microsoftAuthSchema),
            defaultValues: {
                MICROSOFT_CLIENT_ID: initialData?.MICROSOFT_CLIENT_ID || data?.MICROSOFT_CLIENT_ID || "",
                MICROSOFT_CLIENT_SECRET: initialData?.MICROSOFT_CLIENT_SECRET || data?.MICROSOFT_CLIENT_SECRET || "",
                MICROSOFT_TENANT_ID: initialData?.MICROSOFT_TENANT_ID || data?.MICROSOFT_TENANT_ID || "",
            },
        });

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    // Get the first error message
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("Microsoft Auth validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => {
                return form.getValues();
            },
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        // Watch for changes and notify parent
        React.useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });
            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        const handleSubmit = (formData: MicrosoftAuthData) => {
            if (onSubmit) {
                onSubmit(formData);
            }
        };

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <div className="flex items-center gap-2">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                <AlertTitle className="mb-0">{error}</AlertTitle>
                            </div>
                        </Alert>
                    )}

                    <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="MICROSOFT_CLIENT_ID"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel htmlFor={field.name} className="text-sm font-medium">
                                        Microsoft Client ID
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            readOnly={readOnly}
                                            placeholder="Enter your Microsoft Application (client) ID"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="MICROSOFT_CLIENT_SECRET"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel htmlFor={field.name} className="text-sm font-medium">
                                        Microsoft Client Secret
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            type="password"
                                            readOnly={readOnly}
                                            placeholder="Enter your Microsoft Client Secret"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="MICROSOFT_TENANT_ID"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel htmlFor={field.name} className="text-sm font-medium">
                                        Microsoft Tenant ID
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            readOnly={readOnly}
                                            placeholder="Enter your Microsoft Directory (tenant) ID"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="rounded-lg border p-4">
                        <p className="text-sm">
                            <strong>Setup Instructions:</strong>
                        </p>
                        <ol className="mt-2 list-inside list-decimal space-y-1 text-sm">
                            <li>Go to the Azure Portal and navigate to Azure Active Directory</li>
                            <li>Register a new application or use an existing one</li>
                            <li>Copy the Application (client) ID and Directory (tenant) ID</li>
                            <li>Create a new client secret and copy its value</li>
                            <li>Configure redirect URIs for your application</li>
                        </ol>
                    </div>

                    {!hideSubmitButton && (
                        <div className="flex justify-end">
                            {submitButton || (
                                <Button type="submit" disabled={readOnly || loading}>
                                    {loading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
                                    Save Configuration
                                </Button>
                            )}
                        </div>
                    )}
                </form>
            </Form>
        );
    }
);

MicrosoftAuthForm.displayName = "MicrosoftAuthForm";

export default MicrosoftAuthForm;
